import React, { useEffect, useState } from "react";
import { Button, message } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { isMobile } from "react-device-detect";

// iOS only exposes PushManager inside a Home Screen web app, so this also hides
// the button in a normal Safari/Chrome tab on iPhone.
const isPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

const withOneSignal = (callback) => {
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(callback);
};

// Fallback for the OneSignal bell, which the v16 SDK does not render on browsers
// without CSS Anchor Positioning (e.g. iOS Safari before iOS 26).
const PushSubscribeButton = () => {
  const [optedIn, setOptedIn] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return undefined;

    let mounted = true;
    let pushSubscription = null;
    const onChange = (event) => {
      if (mounted) {
        setOptedIn(!!(event && event.current && event.current.optedIn));
      }
    };

    withOneSignal((OneSignal) => {
      if (!mounted) return;
      pushSubscription = OneSignal.User.PushSubscription;
      setOptedIn(!!pushSubscription.optedIn);
      pushSubscription.addEventListener("change", onChange);
    });

    return () => {
      mounted = false;
      if (pushSubscription) {
        pushSubscription.removeEventListener("change", onChange);
      }
    };
  }, []);

  if (!isPushSupported() || optedIn) return null;

  const onEnable = async () => {
    const OneSignal = window.OneSignal;
    if (!OneSignal || !OneSignal.Notifications || !OneSignal.User) {
      message.warning("Notifications are still loading, please try again.");
      return;
    }
    if (window.Notification.permission === "denied") {
      message.error(
        "Notifications are blocked. Allow them in the browser / iPhone settings, then try again."
      );
      return;
    }

    setLoading(true);
    try {
      // Must be called directly from the tap: iOS only shows the permission prompt for a user gesture.
      await OneSignal.Notifications.requestPermission();
      await OneSignal.User.PushSubscription.optIn();
      setOptedIn(!!OneSignal.User.PushSubscription.optedIn);
    } catch (error) {
      console.error(error);
      message.error("Could not enable notifications, please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="primary"
      icon={<BellOutlined />}
      loading={loading}
      onClick={onEnable}
    >
      {isMobile ? "Enable" : "Enable notifications"}
    </Button>
  );
};

export default PushSubscribeButton;
